import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface HashItinerary {
    from: string;
    to: string;
}

/** `#depart:arrivee` (historical URL format, kept for bookmarks). */
export function parseHash(hash: string): HashItinerary | null {
    const parts = decodeURIComponent(hash.replace(/^#/, '')).split(':');
    if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
    return { from: parts[0], to: parts[1] };
}

/**
 * Itinerary of the URL fragment, read from the router location: a menu link (`pushState`, no
 * `hashchange` event) or the back button updates it like a change of the fragment itself.
 */
export function useHashItinerary(): [
    HashItinerary | null,
    (itinerary: HashItinerary, options?: { replace?: boolean }) => void,
] {
    const location = useLocation();
    const navigate = useNavigate();
    // Keyed on the location, not the fragment: setting the same fragment again yields a new value and recomputes.
    const value = useMemo(() => parseHash(location.hash), [location]);

    const set = useCallback(
        (itinerary: HashItinerary, options?: { replace?: boolean }) => {
            const hash = `#${itinerary.from}:${itinerary.to}`;
            navigate({ hash }, { replace: options?.replace || window.location.hash === hash });
        },
        [navigate],
    );

    return [value, set];
}
